/**
 * Flow Matrix — compare mode.
 *
 * Two to six panes, each its own underlying and data set (source, expiry window, strikes,
 * metric, and for the live feed the session or the latest 5 minutes). When every pane is on the
 * same underlying the panes share one strike axis, so the rows line up and a difference column
 * (pane X − pane Y per cell) can be shown; with different underlyings each pane keeps its own
 * strike ladder. Panes sit in a horizontally scrolling strip (each at least 360 px wide), so any
 * number of them lays out without overlapping; the shared-strike grid scrolls on both axes with a
 * sticky strike column and a sticky header block.
 *
 * Request discipline: one request per distinct pane query, run one after another (never a burst),
 * refreshed on one shared timer that skips hidden tabs. Live panes on the same underlying share a
 * single download. Nothing is prefetched in this mode.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import './edge/EdgeScanner.css';
import './FlowMatrixCompare.css';
import './FlowMatrixCompare.ds.css';
import { isDesignSystemOn } from '../theme/mmecDsPreview';
import ToolState from './mmec/ToolState';
import { LanguageContext } from '../contexts/LanguageContext';
import { useHistoricalModal } from '../contexts/HistoricalModalContext';
import {
  atmIndex,
  fetchLadder,
  matrixCacheKey,
  MATRIX_DTE_OPTIONS,
  MATRIX_METRIC_BY_ID,
  MATRIX_STRIKE_OPTIONS,
  MatrixLadder,
  MatrixLiveWindow,
  MatrixMetricDef,
  MatrixQuery,
  MatrixSource,
  metricPeak,
  metricsForSource,
  metricTotals,
  peekLadder,
  sameUnderlying,
} from '../utils/flowMatrixApi';

export const COMPARE_KEY = 'mmecFlowMatrixCompare';
export const MAX_PANES = 6;
const REFRESH_MS = 60000;
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];
const QUICK_SYMBOLS = ['SPX', 'SPY', 'QQQ', 'IWM', 'NVDA', 'TSLA', 'AAPL', 'AMZN', 'META', 'AMD', 'MSFT'];

export interface PaneConfig {
  id: string;
  symbol: string;
  source: MatrixSource;
  dte: string;
  strikeRange: string;
  metric: string;
  liveWindow: MatrixLiveWindow;
}

export interface CompareState {
  open: boolean;
  panes: PaneConfig[];
  sync: boolean;
  align: boolean;
  diff: boolean;
  /** Indices of the two panes the difference column subtracts (first − second). */
  diffPair: [number, number];
}

let paneSeq = 0;
export function newPaneId(): string {
  paneSeq += 1;
  return `p${Date.now().toString(36)}${paneSeq}`;
}

const DTE_VALUES = new Set<string>(MATRIX_DTE_OPTIONS.map((o) => o.value));
const STRIKE_VALUES = new Set<string>(MATRIX_STRIKE_OPTIONS.map((o) => o.value));

function cleanPane(p: any): PaneConfig | null {
  if (!p || typeof p !== 'object') return null;
  const source: MatrixSource = p.source === 'live' ? 'live' : 'stable';
  const metric = MATRIX_METRIC_BY_ID[p.metric]?.sources.includes(source) ? p.metric : 'volume';
  return {
    id: typeof p.id === 'string' && p.id ? p.id : newPaneId(),
    symbol: typeof p.symbol === 'string' && p.symbol.trim() ? p.symbol.trim().toUpperCase() : 'SPY',
    source,
    dte: DTE_VALUES.has(p.dte) ? p.dte : 'weekly',
    strikeRange: STRIKE_VALUES.has(p.strikeRange) ? p.strikeRange : '20',
    metric,
    liveWindow: p.liveWindow === '5min' ? '5min' : 'session',
  };
}

export function loadCompareState(): CompareState | null {
  try {
    const raw = window.localStorage.getItem(COMPARE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    const panes = (Array.isArray(s?.panes) ? s.panes : []).map(cleanPane).filter(Boolean).slice(0, MAX_PANES) as PaneConfig[];
    if (panes.length < 2) return null;
    const pair: [number, number] =
      Array.isArray(s.diffPair) &&
      s.diffPair.length === 2 &&
      s.diffPair.every((i: unknown) => Number.isInteger(i) && (i as number) >= 0 && (i as number) < panes.length) &&
      s.diffPair[0] !== s.diffPair[1]
        ? [s.diffPair[0], s.diffPair[1]]
        : [0, 1];
    return { open: !!s.open, panes, sync: !!s.sync, align: s.align !== false, diff: !!s.diff, diffPair: pair };
  } catch {
    return null;
  }
}

export function saveCompareState(s: CompareState): void {
  try {
    window.localStorage.setItem(COMPARE_KEY, JSON.stringify(s));
  } catch {
    /* compare layout simply does not persist */
  }
}

const COPY = {
  en: {
    title: 'Flow Matrix',
    compare: 'Compare',
    single: 'Single view',
    addPane: 'Add pane',
    remove: 'Remove pane',
    moveBack: 'Move pane back',
    moveForward: 'Move pane forward',
    maxPanes: (n: number) => `Up to ${n} panes`,
    errorTitle: 'This pane could not load.',
    errorRetry: 'Retrying on the next refresh (every 60 s).',
    emptyHint: 'Try another expiry or a wider strike range.',
    symbol: 'Symbol',
    source: 'Source',
    stable: 'Stable',
    live: 'Live',
    window: 'Window',
    session: 'Session',
    fiveMin: 'Last 5 min',
    expiry: 'Expiry',
    strikes: 'Strikes',
    metric: 'Metric',
    sync: 'Sync',
    syncHint: 'Same expiry, strikes, metric and window on every pane; separate ladders scroll together',
    align: 'Shared strikes',
    alignHint: 'One strike axis for panes on the same underlying, rows aligned',
    alignOff: 'Panes are on different underlyings, so each keeps its own strikes',
    diff: 'Difference',
    diffHint: 'Pane minus pane, per strike and side',
    diffNeedsAlign: 'Needs every pane on the same underlying with shared strikes',
    diffNeedsMetric: 'Both panes need the same metric for a difference',
    calls: 'Calls',
    puts: 'Puts',
    strike: 'Strike',
    pc: 'P/C',
    spot: 'spot',
    loading: 'Loading…',
    noData: 'No contracts in this window.',
    no0dte: 'Nothing expires today for this symbol.',
    liveCarries: (d: string) => `The live feed carries ${d} for this symbol — no expiry in this window. Choose All to see it.`,
    liveEmpty: 'The live feed carries nothing for this symbol right now.',
    ago: (s: string) => s,
    strikesN: (n: number) => `${n} strikes`,
    openHistory: (s: string) => `${s} — open contract history`,
    note:
      'Each pane scales its bars to its own largest value. Difference cells scale to the largest gap. ' +
      'The live feed carries the bid/ask split and trade counts; greeks and exposure come from the stable chain. ' +
      'Click a cell to open that contract’s history.',
  },
  ar: {
    title: 'مصفوفة التدفق',
    compare: 'مقارنة',
    single: 'عرض واحد',
    addPane: 'إضافة لوحة',
    remove: 'إزالة اللوحة',
    moveBack: 'نقل اللوحة للخلف',
    moveForward: 'نقل اللوحة للأمام',
    maxPanes: (n: number) => `حتى ${n} لوحات`,
    errorTitle: 'تعذّر تحميل هذه اللوحة.',
    errorRetry: 'ستُعاد المحاولة في التحديث التالي (كل 60 ثانية).',
    emptyHint: 'جرّب استحقاقًا آخر أو نطاق أسعار تنفيذ أوسع.',
    symbol: 'الرمز',
    source: 'المصدر',
    stable: 'مستقر',
    live: 'مباشر',
    window: 'الفترة',
    session: 'الجلسة',
    fiveMin: 'آخر 5 دقائق',
    expiry: 'الاستحقاق',
    strikes: 'أسعار التنفيذ',
    metric: 'المقياس',
    sync: 'مزامنة',
    syncHint: 'نفس الاستحقاق وأسعار التنفيذ والمقياس والفترة في كل اللوحات، والسلالم المنفصلة تتمرر معًا',
    align: 'محور أسعار مشترك',
    alignHint: 'محور واحد لأسعار التنفيذ للوحات على نفس الأصل، والصفوف متطابقة',
    alignOff: 'اللوحات على أصول مختلفة، لذلك لكل لوحة أسعار تنفيذها',
    diff: 'الفرق',
    diffHint: 'لوحة ناقص لوحة، لكل سعر تنفيذ وجهة',
    diffNeedsAlign: 'يتطلب أن تكون كل اللوحات على نفس الأصل مع محور أسعار مشترك',
    diffNeedsMetric: 'يجب أن يكون للوحتين نفس المقياس لحساب الفرق',
    calls: 'كول',
    puts: 'بوت',
    strike: 'سعر التنفيذ',
    pc: 'بوت/كول',
    spot: 'السعر',
    loading: 'جارٍ التحميل…',
    noData: 'لا توجد عقود في هذه الفترة.',
    no0dte: 'لا يوجد استحقاق اليوم لهذا الرمز.',
    liveCarries: (d: string) => `البث المباشر يحمل ${d} لهذا الرمز — لا استحقاق في هذه الفترة. اختر «الكل» لعرضه.`,
    liveEmpty: 'البث المباشر لا يحمل بيانات لهذا الرمز الآن.',
    ago: (s: string) => s.replace('s ago', ' ث').replace('m ago', ' د'),
    strikesN: (n: number) => `${n} سعر تنفيذ`,
    openHistory: (s: string) => `${s} — فتح سجل العقد`,
    note:
      'كل لوحة تقيس أعمدتها على أكبر قيمة فيها. خلايا الفرق تقاس على أكبر فجوة. ' +
      'البث المباشر يحمل تقسيم العرض/الطلب وعدد الصفقات؛ اليونانيات والتعرض من السلسلة المستقرة. ' +
      'اضغط على خلية لفتح سجل العقد.',
  },
};
type Copy = typeof COPY.en;

const DTE_AR: Record<string, string> = { weekly: 'أسبوعي', monthly: 'شهري', all: 'الكل' };
const STRIKE_AR: Record<string, string> = { all: 'الكل' };

function relativeAge(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return `${s}s ago`;
  return `${Math.round(s / 60)}m ago`;
}

function shortDate(iso: string): string {
  return iso.length >= 10 ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}` : iso;
}

function toQuery(p: PaneConfig): MatrixQuery {
  return p.source === 'live'
    ? { symbol: p.symbol, dte: p.dte, strikeRange: p.strikeRange, source: 'live', liveWindow: p.liveWindow }
    : { symbol: p.symbol, dte: p.dte, strikeRange: p.strikeRange };
}

interface PaneData {
  ladder: MatrixLadder | null;
  loading: boolean;
  error: string | null;
}

/** Fill colour class for a value cell: Bid red / Ask green, signed metrics by sign, else by side. */
function toneClass(def: MatrixMetricDef, side: 'call' | 'put', v: number): string {
  if (def.tone === 'bid') return 'is-bid';
  if (def.tone === 'ask') return 'is-ask';
  if (def.signed) return v < 0 ? 'is-neg' : 'is-pos';
  return side === 'call' ? 'is-call' : 'is-put';
}

function fmtSigned(def: MatrixMetricDef, v: number): string {
  const s = def.format(v);
  return v > 0 && !s.startsWith('+') ? `+${s}` : s;
}

interface CellProps {
  value: number | null;
  peak: number;
  def: MatrixMetricDef;
  side: 'call' | 'put';
  diff?: boolean;
  title?: string;
  onClick?: () => void;
}

const Cell: React.FC<CellProps> = ({ value, peak, def, side, diff, title, onClick }) => {
  const v = value ?? 0;
  const pct = peak > 0 && v ? Math.min(100, (Math.abs(v) / peak) * 100) : 0;
  const tone = diff ? (v < 0 ? 'is-neg' : 'is-pos') : toneClass(def, side, v);
  const text = value == null ? '—' : v ? (diff || def.signed ? fmtSigned(def, v) : def.format(v)) : '';
  const inner = (
    <>
      <span className={`fmc-fill ${tone}`} style={{ width: `${pct}%` }} />
      <span className="fmc-val edge-num">{text}</span>
    </>
  );
  const cls = `fmc-cell fmc-${side}${diff ? ' is-diff' : ''}`;
  if (onClick && value != null) {
    return (
      <button type="button" className={cls} title={title} onClick={onClick}>
        {inner}
      </button>
    );
  }
  return <span className={cls}>{inner}</span>;
};

export interface FlowMatrixCompareProps {
  state: CompareState;
  onChange: (next: CompareState) => void;
  onExit: () => void;
  simpleLayout?: boolean;
}

const FlowMatrixCompare: React.FC<FlowMatrixCompareProps> = ({ state, onChange, onExit, simpleLayout = false }) => {
  const lang = useContext(LanguageContext)?.language ?? 'en';
  const isArabic = lang === 'ar';
  const c: Copy = isArabic ? (COPY.ar as unknown as Copy) : COPY.en;
  const { openHistorical } = useHistoricalModal();

  const { panes, sync, align, diff, diffPair } = state;

  // ── Data: one request per distinct query, sequential, one shared refresh timer ─────────────
  const queries = useMemo(() => panes.map((p) => toQuery(p)), [panes]);
  const keys = useMemo(() => queries.map(matrixCacheKey), [queries]);
  const keySig = keys.join('~');
  const [byKey, setByKey] = useState<Record<string, PaneData>>(() => {
    const init: Record<string, PaneData> = {};
    queries.forEach((q, i) => {
      init[keys[i]] = { ladder: peekLadder(q), loading: false, error: null };
    });
    return init;
  });
  const [, setClock] = useState(0);

  useEffect(() => {
    const unique = new Map<string, MatrixQuery>();
    queries.forEach((q, i) => unique.set(keys[i], q));
    let cancelled = false;
    let running = false;
    const controller = new AbortController();

    setByKey((prev) => {
      const next: Record<string, PaneData> = {};
      unique.forEach((q, k) => {
        const ladder = prev[k]?.ladder ?? peekLadder(q);
        next[k] = { ladder, loading: !ladder, error: null };
      });
      return next;
    });

    const run = async () => {
      if (running) return;
      running = true;
      try {
        for (const [k, q] of Array.from(unique.entries())) {
          if (cancelled) return;
          try {
            const ladder = await fetchLadder(q, controller.signal);
            if (!cancelled) setByKey((prev) => ({ ...prev, [k]: { ladder, loading: false, error: null } }));
          } catch (e) {
            if (cancelled || controller.signal.aborted || (e as Error)?.name === 'AbortError') return;
            const msg = e instanceof Error ? e.message : 'Could not load the chain';
            setByKey((prev) => ({ ...prev, [k]: { ladder: prev[k]?.ladder ?? null, loading: false, error: msg } }));
          }
        }
      } finally {
        running = false;
        if (!cancelled) setClock((n) => n + 1);
      }
    };

    run();
    const timer = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      run();
    }, REFRESH_MS);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(timer);
    };
    // keySig captures every query field; queries/keys are derived from the same panes.
  }, [keySig]);

  const paneData = (i: number): PaneData => byKey[keys[i]] ?? { ladder: null, loading: true, error: null };

  // ── Derived layout facts ────────────────────────────────────────────────────────────────
  const allSame = panes.every((p) => sameUnderlying(p.symbol, panes[0].symbol));
  const aligned = align && allSame;
  const [da, db] = diffPair[0] < panes.length && diffPair[1] < panes.length ? diffPair : ([0, 1] as [number, number]);
  const diffMetricOk = panes[da]?.metric === panes[db]?.metric;
  const diffAvailable = aligned && diffMetricOk;
  const showDiff = diff && diffAvailable;
  const diffReason = !aligned ? c.diffNeedsAlign : !diffMetricOk ? c.diffNeedsMetric : c.diffHint;

  // ── Pane edits (sync propagates the data set, never the underlying or source) ─────────
  const setPanes = (next: PaneConfig[], extra: Partial<CompareState> = {}) => onChange({ ...state, ...extra, panes: next });

  const updatePane = (idx: number, patch: Partial<PaneConfig>) => {
    let next = panes.map((p, i) => (i === idx ? fixMetric({ ...p, ...patch }) : p));
    if (sync) {
      const shared: Partial<PaneConfig> = {};
      (['dte', 'strikeRange', 'metric', 'liveWindow'] as const).forEach((k) => {
        if (k in patch) (shared as any)[k] = (patch as any)[k];
      });
      if (Object.keys(shared).length) {
        next = next.map((p, i) => (i === idx ? p : fixMetric({ ...p, ...shared }, p.metric)));
      }
    }
    setPanes(next);
  };

  const toggleSync = () => {
    if (sync) {
      onChange({ ...state, sync: false });
      return;
    }
    const a = panes[0];
    const next = panes.map((p, i) =>
      i === 0 ? p : fixMetric({ ...p, dte: a.dte, strikeRange: a.strikeRange, metric: a.metric, liveWindow: a.liveWindow }, p.metric),
    );
    onChange({ ...state, sync: true, panes: next });
  };

  const addPane = () => {
    if (panes.length >= MAX_PANES) return;
    const last = panes[panes.length - 1];
    setPanes([...panes, { ...last, id: newPaneId() }]);
  };

  /** Swap pane idx with its neighbour; the difference pair follows the panes it named. */
  const movePane = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= panes.length) return;
    const next = panes.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    const f = (k: number) => (k === idx ? j : k === j ? idx : k);
    setPanes(next, { diffPair: [f(da), f(db)] });
  };

  const removePane = (idx: number) => {
    const next = panes.filter((_, i) => i !== idx);
    if (next.length < 2) {
      onExit();
      return;
    }
    // Keep the difference pair on the same panes when both survive.
    const f = (k: number) => (k > idx ? k - 1 : k);
    const keep = da !== idx && db !== idx;
    setPanes(next, { diffPair: keep ? [f(da), f(db)] : [0, 1] });
  };

  // ── Scroll sync for separate ladders: keep the same offset from each pane's ATM row ──────
  const scrollRefs = useRef<Array<HTMLDivElement | null>>([]);
  const syncing = useRef(false);
  const onPaneScroll = useCallback(
    (idx: number) => {
      if (!sync || syncing.current) return;
      const src = scrollRefs.current[idx];
      if (!src) return;
      const atmTop = (el: HTMLDivElement) => el.querySelector<HTMLElement>('.fmc-row.is-atm')?.offsetTop ?? 0;
      const offset = src.scrollTop - atmTop(src);
      syncing.current = true;
      scrollRefs.current.forEach((el, i) => {
        if (el && i !== idx) el.scrollTop = atmTop(el) + offset;
      });
      window.requestAnimationFrame(() => {
        syncing.current = false;
      });
    },
    [sync],
  );

  // Centre on the money when a ladder first arrives for a query.
  const centred = useRef<Set<string>>(new Set());
  const gridRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const containers = aligned ? [gridRef.current] : scrollRefs.current;
    const sig = aligned ? `aligned|${keySig}` : null;
    containers.forEach((el, i) => {
      if (!el) return;
      const k = sig ?? `sep|${keys[i]}`;
      if (centred.current.has(k)) return;
      const row = el.querySelector<HTMLElement>('.fmc-row.is-atm');
      if (!row) return;
      // Centre within the area below the sticky header block.
      const head = el.querySelector<HTMLElement>('.fmc-stickyhead, .fmc-head3')?.offsetHeight ?? 0;
      el.scrollTop = Math.max(0, row.offsetTop - head - (el.clientHeight - head) / 2 + row.clientHeight / 2);
      centred.current.add(k);
    });
  });

  // Bring a newly added pane (or a newly shown difference column) into view inside its strip.
  const stripRef = useRef<HTMLDivElement | null>(null);
  const prevShape = useRef({ n: panes.length, diff: showDiff });
  useEffect(() => {
    const prev = prevShape.current;
    prevShape.current = { n: panes.length, diff: showDiff };
    if (panes.length <= prev.n && !(showDiff && !prev.diff)) return;
    const el = aligned ? gridRef.current : stripRef.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    const end = isArabic ? -el.scrollWidth : el.scrollWidth;
    el.scrollTo({ left: end, behavior: 'smooth' });
  }, [panes.length, showDiff, aligned, isArabic]);

  const openContract = (p: PaneConfig, strike: number, expiry: string, side: 'call' | 'put') => {
    if (!expiry) return;
    openHistorical(
      {
        underlying: p.symbol,
        strike,
        expiry,
        optionType: side === 'put' ? 'PUT' : 'CALL',
        source: p.metric === 'oi' ? 'oi' : 'volume',
      },
      { compactChrome: simpleLayout },
    );
  };

  const metricLabel = (def: MatrixMetricDef) => (isArabic ? def.labelAr : def.label);
  const dteLabel = (v: string, l: string) => (isArabic ? DTE_AR[v] ?? l : l);
  const strikeLabel = (v: string, l: string) => (isArabic ? STRIKE_AR[v] ?? l : l);

  const emptyMessage = (p: PaneConfig, ladder: MatrixLadder): string => {
    if (p.source === 'live') {
      const fe = ladder.feedExpiries ?? [];
      return fe.length ? c.liveCarries(fe.map(shortDate).join(', ')) : c.liveEmpty;
    }
    return p.dte === '0dte' ? `${c.noData} ${c.no0dte}` : c.noData;
  };

  // ── Compact pane header: letter · symbol · source · reorder/remove, then the data set ───
  const renderHead = (p: PaneConfig, idx: number) => {
    const d = paneData(idx);
    const def = MATRIX_METRIC_BY_ID[p.metric] ?? MATRIX_METRIC_BY_ID.volume;
    const totals = d.ladder ? metricTotals(d.ladder, def.id) : null;
    const listId = `fmc-sym-${p.id}`;
    return (
      <div className="fmc-card-head">
        <div className="fmc-card-top">
          <span className="fmc-letter">{LETTERS[idx]}</span>
          <input
            className="fmc-sym edge-num"
            type="text"
            list={listId}
            defaultValue={p.symbol}
            key={`${p.id}-${p.symbol}`}
            aria-label={c.symbol}
            title={c.symbol}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            onBlur={(e) => {
              const s = e.target.value.trim().toUpperCase();
              if (s && s !== p.symbol) updatePane(idx, { symbol: s });
              else e.target.value = p.symbol;
            }}
          />
          <datalist id={listId}>
            {QUICK_SYMBOLS.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
          <div className="fmc-seg" role="group" aria-label={c.source}>
            {(['stable', 'live'] as MatrixSource[]).map((s) => (
              <button
                key={s}
                type="button"
                className={p.source === s ? 'is-on' : ''}
                aria-pressed={p.source === s}
                onClick={() => p.source !== s && updatePane(idx, { source: s })}
              >
                {s === 'stable' ? c.stable : c.live}
              </button>
            ))}
          </div>
          <div className="fmc-tools">
            <button
              type="button"
              className="fmc-icon"
              title={c.moveBack}
              aria-label={c.moveBack}
              disabled={idx === 0}
              onClick={() => movePane(idx, -1)}
            >
              {isArabic ? '›' : '‹'}
            </button>
            <button
              type="button"
              className="fmc-icon"
              title={c.moveForward}
              aria-label={c.moveForward}
              disabled={idx === panes.length - 1}
              onClick={() => movePane(idx, 1)}
            >
              {isArabic ? '‹' : '›'}
            </button>
            <button type="button" className="fmc-icon fmc-x" title={c.remove} aria-label={c.remove} onClick={() => removePane(idx)}>
              ×
            </button>
          </div>
        </div>
        <div className="fmc-card-ctl">
          <select value={p.dte} aria-label={c.expiry} title={c.expiry} onChange={(e) => updatePane(idx, { dte: e.target.value })}>
            {MATRIX_DTE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {dteLabel(o.value, o.label)}
              </option>
            ))}
          </select>
          <select
            value={p.strikeRange}
            aria-label={c.strikes}
            title={c.strikes}
            onChange={(e) => updatePane(idx, { strikeRange: e.target.value })}
          >
            {MATRIX_STRIKE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {strikeLabel(o.value, o.label)}
              </option>
            ))}
          </select>
          {p.source === 'live' && (
            <select
              value={p.liveWindow}
              aria-label={c.window}
              title={c.window}
              onChange={(e) => updatePane(idx, { liveWindow: e.target.value as MatrixLiveWindow })}
            >
              <option value="session">{c.session}</option>
              <option value="5min">{c.fiveMin}</option>
            </select>
          )}
          <select
            className="fmc-metric"
            value={def.id}
            aria-label={c.metric}
            title={c.metric}
            onChange={(e) => updatePane(idx, { metric: e.target.value })}
          >
            {metricsForSource(p.source).map((m) => (
              <option key={m.id} value={m.id}>
                {metricLabel(m)}
              </option>
            ))}
          </select>
        </div>
        <div className="fmc-card-stats">
          {totals && (
            <>
              <span className="fmc-stat is-call">
                {c.calls} <b className="edge-num">{def.signed ? fmtSigned(def, totals.call) : def.format(totals.call)}</b>
              </span>
              <span className="fmc-stat is-put">
                {c.puts} <b className="edge-num">{def.signed ? fmtSigned(def, totals.put) : def.format(totals.put)}</b>
              </span>
              <span className="fmc-stat">
                {c.pc} <b className="edge-num">{totals.ratio == null ? '—' : totals.ratio.toFixed(2)}</b>
              </span>
            </>
          )}
          {d.ladder && d.ladder.spot > 0 && (
            <span className="fmc-stat">
              {c.spot} <b className="edge-num">{d.ladder.spot.toFixed(2)}</b>
            </span>
          )}
          <span className="fmc-age">
            {d.loading && !d.ladder
              ? c.loading
              : d.ladder
                ? `${c.strikesN(d.ladder.rows.length)} · ${c.ago(relativeAge(d.ladder.fetchedAt))}`
                : '—'}
          </span>
        </div>
      </div>
    );
  };

  /** Loading / error / empty state for one pane, or null when it has rows to show. */
  const paneState = (p: PaneConfig, d: PaneData, compact = false) => {
    const cls = `fmc-empty${compact ? ' is-compact' : ''}`;
    if (isDesignSystemOn() && !compact) {
      const lg = isArabic ? 'ar' : 'en';
      if (!d.ladder && d.loading) return <ToolState kind="loading" lang={lg} rows={6} />;
      if (!d.ladder && d.error) return <ToolState kind="error" lang={lg} title={c.errorTitle} body={<span title={d.error}>{c.errorRetry}</span>} />;
      if (d.ladder && !d.ladder.rows.length) return <ToolState kind="empty" lang={lg} title={emptyMessage(p, d.ladder)} body={c.emptyHint} />;
    }
    if (!d.ladder && d.loading) {
      return (
        <div className={cls} role="status">
          <span className="fmc-spinner" aria-hidden="true" />
          {c.loading}
        </div>
      );
    }
    if (!d.ladder && d.error) {
      return (
        <div className={`${cls} is-error`} role="alert">
          <b>{c.errorTitle}</b>
          <span title={d.error}>{c.errorRetry}</span>
        </div>
      );
    }
    if (d.ladder && !d.ladder.rows.length) {
      return (
        <div className={cls}>
          <b>{emptyMessage(p, d.ladder)}</b>
          <span>{c.emptyHint}</span>
        </div>
      );
    }
    if (d.error) return <div className="fmc-error" title={d.error}>{c.errorTitle} {c.errorRetry}</div>;
    return null;
  };

  // ── Separate ladders (different underlyings, or shared strikes turned off) ──────────────
  // One column per pane in a horizontally scrolling strip; each pane owns its vertical scroller,
  // so its sticky header is scoped to that pane and can never sit over a neighbour.
  const renderSeparate = () => (
    <div className="fmc-strip" ref={stripRef} role="region" aria-label={c.compare}>
      {panes.map((p, idx) => {
        const d = paneData(idx);
        const def = MATRIX_METRIC_BY_ID[p.metric] ?? MATRIX_METRIC_BY_ID.volume;
        const ladder = d.ladder;
        const peak = ladder ? metricPeak(ladder, def.id) : 0;
        const atm = ladder ? atmIndex(ladder) : -1;
        const state = paneState(p, d);
        return (
          <section className="fmc-card" key={p.id} aria-label={`${LETTERS[idx]} ${p.symbol}`}>
            {renderHead(p, idx)}
            <div
              className="fmc-ladder"
              ref={(el) => {
                scrollRefs.current[idx] = el;
              }}
              onScroll={() => onPaneScroll(idx)}
            >
              <div className="fmc-row fmc-head3">
                <span className="fmc-hcell is-call">{c.calls}</span>
                <span className="fmc-hcell fmc-strike">{c.strike}</span>
                <span className="fmc-hcell is-put">{c.puts}</span>
              </div>
              {state}
              {ladder?.rows.map((row, i) => (
                <div className={`fmc-row fmc-row3${i === atm ? ' is-atm' : ''}`} key={row.strike}>
                  <Cell
                    value={row.call[def.id] ?? 0}
                    peak={peak}
                    def={def}
                    side="call"
                    title={c.openHistory(`${p.symbol} ${row.strike} C`)}
                    onClick={() => openContract(p, row.strike, row.expiry, 'call')}
                  />
                  <span className="fmc-strike edge-num">{row.strike}</span>
                  <Cell
                    value={row.put[def.id] ?? 0}
                    peak={peak}
                    def={def}
                    side="put"
                    title={c.openHistory(`${p.symbol} ${row.strike} P`)}
                    onClick={() => openContract(p, row.strike, row.expiry, 'put')}
                  />
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );

  // ── One grid on a shared strike axis ────────────────────────────────────────────────────
  // A single scroller (both axes): the strike column sticks to the inline start, and one sticky
  // block holds the pane headers plus the Calls/Puts row, so nothing floats over the cells.
  const renderAligned = () => {
    const ladders = panes.map((_, i) => paneData(i).ladder);
    const defs = panes.map((p) => MATRIX_METRIC_BY_ID[p.metric] ?? MATRIX_METRIC_BY_ID.volume);
    const peaks = ladders.map((l, i) => (l ? metricPeak(l, defs[i].id) : 0));
    const lookups = ladders.map((l) => {
      const m = new Map<number, MatrixLadder['rows'][number]>();
      l?.rows.forEach((r) => m.set(r.strike, r));
      return m;
    });
    const strikes = Array.from(new Set(ladders.flatMap((l) => (l ? l.rows.map((r) => r.strike) : [])))).sort((a, b) => a - b);
    const spot = ladders.find((l) => l && l.spot > 0)?.spot ?? 0;
    let atmStrike: number | null = null;
    if (spot && strikes.length) {
      atmStrike = strikes.reduce((best, k) => (Math.abs(k - spot) < Math.abs(best - spot) ? k : best), strikes[0]);
    }

    const diffDef = defs[da];
    const diffVal = (strike: number, side: 'call' | 'put'): number | null => {
      const ra = lookups[da].get(strike);
      const rb = lookups[db].get(strike);
      if (!ra || !rb) return null;
      return (ra[side][diffDef.id] ?? 0) - (rb[side][diffDef.id] ?? 0);
    };
    let diffPeak = 0;
    if (showDiff) {
      strikes.forEach((k) => {
        diffPeak = Math.max(diffPeak, Math.abs(diffVal(k, 'call') ?? 0), Math.abs(diffVal(k, 'put') ?? 0));
      });
    }

    const groups = panes.length + (showDiff ? 1 : 0);
    const firstState = strikes.length ? null : paneState(panes[0], paneData(0));

    return (
      <div className="fmc-gridwrap" ref={gridRef} role="region" aria-label={c.compare}>
        <div className="fmc-grid" style={{ ['--fmc-cols' as any]: `var(--fmc-strike-w) repeat(${groups * 2}, minmax(var(--fmc-cell-min), 1fr))` }}>
          <div className="fmc-stickyhead">
            <div className="fmc-row fmc-cardrow">
              <span className="fmc-corner fmc-sticky" />
              {panes.map((p, idx) => {
                const d = paneData(idx);
                const st = d.ladder && d.ladder.rows.length ? (d.error ? <div className="fmc-error" title={d.error}>{c.errorTitle} {c.errorRetry}</div> : null) : paneState(p, d, true);
                return (
                  <section className="fmc-card fmc-card-inline" key={p.id} aria-label={`${LETTERS[idx]} ${p.symbol}`}>
                    {renderHead(p, idx)}
                    {st}
                  </section>
                );
              })}
              {showDiff && (
                <section className="fmc-card fmc-card-inline fmc-diffcard">
                  <div className="fmc-card-head">
                    <div className="fmc-card-top">
                      <span className="fmc-letter is-diff">Δ</span>
                      <span className="fmc-diff-title">{c.diff}</span>
                      <select
                        className="fmc-pair edge-num"
                        value={`${da}-${db}`}
                        aria-label={c.diff}
                        onChange={(e) => {
                          const [x, y] = e.target.value.split('-').map(Number);
                          onChange({ ...state, diffPair: [x, y] });
                        }}
                      >
                        {pairOptions.map(([x, y]) => (
                          <option key={`${x}-${y}`} value={`${x}-${y}`}>
                            {LETTERS[x]} − {LETTERS[y]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="fmc-card-stats">
                      <span>{c.diffHint}</span>
                      <span className="fmc-age">{metricLabel(diffDef)}</span>
                    </div>
                  </div>
                </section>
              )}
            </div>
            <div className="fmc-row fmc-shead">
              <span className="fmc-hcell fmc-strike fmc-sticky">{c.strike}</span>
              {Array.from({ length: groups }).map((_, g) => (
                <React.Fragment key={g}>
                  <span className={`fmc-hcell is-call fmc-gstart${g === panes.length ? ' is-diff' : ''}`}>
                    {g < panes.length ? `${LETTERS[g]} · ${c.calls}` : c.calls}
                  </span>
                  <span className="fmc-hcell is-put">{c.puts}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
          {firstState && <div className="fmc-gridstate">{firstState}</div>}
          {strikes.map((k) => (
            <div className={`fmc-row${k === atmStrike ? ' is-atm' : ''}`} key={k}>
              {/* edge-num (direction: ltr) lives on an inner span: on the sticky cell itself it would flip
                  inset-inline-start to the left edge in Arabic. */}
              <span className="fmc-strike fmc-sticky">
                <span className="edge-num">{k}</span>
              </span>
              {panes.map((p, i) => {
                const r = lookups[i].get(k);
                return (
                  <React.Fragment key={p.id}>
                    <Cell
                      value={r ? r.call[defs[i].id] ?? 0 : null}
                      peak={peaks[i]}
                      def={defs[i]}
                      side="call"
                      title={c.openHistory(`${p.symbol} ${k} C`)}
                      onClick={r ? () => openContract(p, k, r.expiry, 'call') : undefined}
                    />
                    <Cell
                      value={r ? r.put[defs[i].id] ?? 0 : null}
                      peak={peaks[i]}
                      def={defs[i]}
                      side="put"
                      title={c.openHistory(`${p.symbol} ${k} P`)}
                      onClick={r ? () => openContract(p, k, r.expiry, 'put') : undefined}
                    />
                  </React.Fragment>
                );
              })}
              {showDiff && (
                <>
                  <Cell value={diffVal(k, 'call')} peak={diffPeak} def={diffDef} side="call" diff />
                  <Cell value={diffVal(k, 'put')} peak={diffPeak} def={diffDef} side="put" diff />
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Every ordered pair of panes, first − second.
  const pairOptions: Array<[number, number]> = [];
  panes.forEach((_, x) =>
    panes.forEach((__, y) => {
      if (x !== y) pairOptions.push([x, y]);
    }),
  );
  const atMax = panes.length >= MAX_PANES;

  return (
    <div className={`edge-root fmc-root${simpleLayout ? ' is-simple' : ''}`} dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="edge-head">
        <div>
          <div className="edge-eyebrow">{c.compare}</div>
          <h1 className="edge-title">
            {c.title} <span className="fmc-title-syms edge-num">· {Array.from(new Set(panes.map((p) => p.symbol))).join(' / ')}</span>
          </h1>
        </div>
        <div className="fmc-head-actions">
          <button
            type="button"
            className="edge-btn fmc-add"
            onClick={addPane}
            disabled={atMax}
            title={atMax ? c.maxPanes(MAX_PANES) : c.addPane}
          >
            + {c.addPane} <span className="fmc-count edge-num">{panes.length}/{MAX_PANES}</span>
          </button>
          <button type="button" className="edge-btn" onClick={onExit}>
            {c.single}
          </button>
        </div>
      </div>

      <div className="edge-filters fmc-toolbar">
        <button
          type="button"
          className={`edge-toggle${sync ? ' is-on' : ''}`}
          aria-pressed={sync}
          title={c.syncHint}
          onClick={toggleSync}
        >
          <span className="edge-toggle-track" />
          {c.sync}
        </button>
        <button
          type="button"
          className={`edge-toggle${aligned ? ' is-on' : ''}`}
          aria-pressed={aligned}
          disabled={!allSame}
          title={allSame ? c.alignHint : c.alignOff}
          onClick={() => onChange({ ...state, align: !align })}
        >
          <span className="edge-toggle-track" />
          {c.align}
        </button>
        <button
          type="button"
          className={`edge-toggle${showDiff ? ' is-on' : ''}`}
          aria-pressed={showDiff}
          disabled={!diffAvailable}
          title={diffReason}
          onClick={() => onChange({ ...state, diff: !diff })}
        >
          <span className="edge-toggle-track" />
          {c.diff}
        </button>
        <span className="edge-filters-right">{!allSame ? c.alignOff : !diffAvailable && diff ? diffReason : ''}</span>
      </div>

      {aligned ? renderAligned() : renderSeparate()}

      <div className="edge-foot">
        <span>{c.note}</span>
      </div>
    </div>
  );
};

/** Keep a pane's metric valid for its source; when it is not, fall back to `prefer` or volume. */
function fixMetric(p: PaneConfig, prefer?: string): PaneConfig {
  const ok = (id?: string) => !!id && !!MATRIX_METRIC_BY_ID[id]?.sources.includes(p.source);
  if (ok(p.metric)) return p;
  return { ...p, metric: ok(prefer) ? (prefer as string) : 'volume' };
}

export default FlowMatrixCompare;
